/**
 * balance-bombardment.ts — tunable constants for the bombardment + Loyalty
 * pipeline (v2M5).
 *
 * Source of truth for placeholder numbers that the v2M7 balance pass will
 * tune from playtest signal. Per PLANETS §2.7 + STRUCTURES §8 the formulas
 * are locked; only these scalars and the per-size sim radius are open.
 */

import type { PlanetSize } from '../state/types/index';

/**
 * Damage-to-population conversion. Each unit of weapon damage delivered to
 * a planet kills state.balanceData.bombardment.popKillFactor units of Pop.
 * Pop scale is 100 × Size (PLANETS §2.1), so a Size-15 planet caps at
 * 1500 Pop.
 *
 * Placeholder: 0.1. Tuned in v2M7. Calibrated so a single Cruiser firing
 * sustained at a Size-10 (cap=1000) Gaia world over a few minutes can
 * realistically empty it — but a defended planet with Shields/Bunkers in
 * v2M6 lasts much longer.
 *
 * v3M10.1: migrated to state.balanceData.bombardment.popKillFactor.
 */

/**
 * Loyalty drop is computed once every state.balanceData.bombardment.batchTicks
 * (1 game second at 1× speed). Ticks within a window accumulate Pop kills;
 * on the boundary tick the formula `ceil(2 × kills / pop_at_window_start ×
 * 100)` resolves to a concrete drop. The 10-tick cadence aligns with the
 * Loyalty regen rate (+1 per 10 ticks), so a planet alternating between
 * "under bombardment" and "regenerating" sees both effects step at the
 * same boundary.
 *
 * v3M10.1: migrated to state.balanceData.bombardment.batchTicks.
 */

/**
 * Loyalty regen rate in points per state.balanceData.bombardment.batchTicks,
 * capped at 100. Per PLANETS §2.7 lock: +1 per 10 ticks.
 *
 * v3M10.1: migrated to state.balanceData.bombardment.loyaltyRegenPerBatch.
 */

/**
 * Loyalty starting and cap value.
 *
 * v3M10.1: migrated to state.balanceData.bombardment.loyaltyMax.
 */

/**
 * Per-size planet radius in sim coordinates (the 0..1 in-system frame
 * shared by ship sx/sy and planet systemX/systemY). Used by
 * `inBombardmentRange(ship, planet, weapon)`:
 *
 *   inRange(ship, planet, weapon) ⟺
 *     dist(ship, planetCenter) ≤ weapon.range + planetSimRadiusForSize(size)
 *
 * Concrete numbers chosen so even short-range Mk-1 weapons can engage a
 * planet of any size from a reasonable approach distance — without making
 * the planet a comically fat target. Rough check: a Mk-1 weapon range of
 * ~0.05 plus a medium planet's 0.04 radius gives a ~0.09 engagement
 * envelope, well within the 0.18 patrol radius the AI uses.
 *
 * Tuned in v2M7.
 */
/**
 * MFix1.4: PlanetSize is now a number in [5, 20]. Sim radius
 * interpolates linearly from 0.03 (size 5) to 0.07 (size 20).
 * Pre-MFix1.4 values were small=0.03, medium=0.05, large=0.07; the
 * linear formula reaches the same endpoints + smooths the middle.
 * Default home worlds (size 15) get radius ≈0.057.
 */
export function planetSimRadiusForSize(size: PlanetSize): number {
  return 0.03 + ((size - 5) / 15) * 0.04;
}

// ---------------------------------------------------------------------------
// v2M6 — defensive damage chain (STRUCTURES §2.2 + §8)
// ---------------------------------------------------------------------------

/**
 * Per-level damage reduction for Planetary Shields (locked 2026-04-26 for
 * v2M6). L1 = 10% reduction, L5 = 50%, capped — Shields can't be upgraded
 * past L5 (see balance-structures.ts MAX_LEVEL_BY_KIND).
 *
 * v3M10.1: migrated to state.balanceData.bombardment.damageReductionPerShieldLevel
 * and state.balanceData.bombardment.maxShieldLevel.
 */

/**
 * Per-level damage reduction for Re-enforced Underground Bunkers (locked
 * 2026-04-26 for v2M6). Same shape as Shields. Applied AFTER shields and
 * multiplicatively, so a L5 Shield + L5 Bunker stack gives
 * `1 - (0.5 × 0.5) = 75%` total reduction.
 *
 * v3M10.1: migrated to state.balanceData.bombardment.damageReductionPerBunkerLevel
 * and state.balanceData.bombardment.maxBunkerLevel.
 */

/**
 * Casualty / structure damage split, applied AFTER the shield+bunker
 * reduction chain (STRUCTURES §8.2 — locked 2026-04-26 for v2M6).
 *
 * Intent: when non-bunker structures exist, 30% of post-reduction damage
 * lands on one of them (weighted by remaining hull). When none exist,
 * the entire 100% lands on Pop — the share doesn't evaporate. Without
 * this fall-back the player would have a perverse incentive to *not*
 * build structures for defensive Pop-survivability.
 *
 * v3M10.1: migrated to state.balanceData.bombardment.popDamageFraction
 * and state.balanceData.bombardment.structureDamageFraction.
 */

